import React, { useState, useEffect } from 'react';
import {
  TrendingUp, DollarSign, Package, ShoppingCart, Calendar,
  Download, FileText, BarChart3,
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { RevenueReport, ProductionReport, InventoryReport, SalesReport } from '../types/modules';

const COLORS = ['#0891B2', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#6366F1'];

const presets = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
];

const Reporting: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('revenue');
  const [preset, setPreset] = useState<string>('last_30_days');
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  
  // Report data states
  const [revenueData, setRevenueData] = useState<RevenueReport | null>(null);
  const [productionData, setProductionData] = useState<ProductionReport | null>(null);
  const [inventoryData, setInventoryData] = useState<InventoryReport | null>(null);
  const [salesData, setSalesData] = useState<SalesReport | null>(null);

  useEffect(() => {
    fetchReport();
  }, [activeTab, preset]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      if (activeTab === 'revenue') {
        const res = await api.get(`/reports/revenue?preset=${preset}`);
        setRevenueData(res.data);
      } else if (activeTab === 'production') {
        const res = await api.get(`/reports/production?preset=${preset}`);
        const data = res.data;
        // Transform data: rename 'style' to 'name' and remove original 'style' to avoid Recharts style prop conflict
        data.batches_by_style = data.batches_by_style.map((item: any, idx: number) => {
          const { style, ...rest } = item; // Destructure to remove 'style'
          return {
            ...rest,
            name: style, // Use 'name' instead of 'style'
            fill: COLORS[idx % COLORS.length]
          };
        });
        setProductionData(data);
      } else if (activeTab === 'inventory') {
        const res = await api.get(`/reports/inventory`);
        const data = res.data;
        // Add fill colors to items_by_category for Pie chart
        data.items_by_category = data.items_by_category.map((item: any, idx: number) => ({
          ...item,
          fill: COLORS[idx % COLORS.length]
        }));
        setInventoryData(data);
      } else if (activeTab === 'sales') {
        const res = await api.get(`/reports/sales?preset=${preset}`);
        setSalesData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: string): Promise<void> => {
    setExporting(true);
    try {
      const response = await api.post('/reports/export', {
        report_type: activeTab,
        format: format,
        preset: preset,
      }, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeTab}_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-500 mt-1">Generate insights from your business data</p>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Date Range Selector */}
          <div className="flex items-center gap-3">
            <Calendar size={20} className="text-gray-400" />
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0891B2] focus:border-transparent text-sm"
            >
              {presets.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {/* Export Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportReport('csv')}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <Download size={16} />
              CSV
            </button>
            <button
              onClick={() => exportReport('pdf')}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-[#0891B2] hover:bg-[#0891B2]/90 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <FileText size={16} />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max sm:min-w-0">
            {[
              { id: 'revenue', label: 'Revenue', icon: DollarSign },
              { id: 'production', label: 'Production', icon: BarChart3 },
              { id: 'inventory', label: 'Inventory', icon: Package },
              { id: 'sales', label: 'Sales', icon: ShoppingCart },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#0891B2] text-[#0891B2] bg-cyan-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#0891B2] border-r-transparent"></div>
              <p className="text-gray-500 mt-2">Loading report...</p>
            </div>
          ) : (
            <>
              {/* Revenue Tab */}
              {activeTab === 'revenue' && revenueData && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4 border border-cyan-200">
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${revenueData.total_revenue.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                      <p className="text-2xl font-bold text-gray-900">{revenueData.total_orders}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <p className="text-sm text-gray-600 mb-1">Avg Order Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${revenueData.average_order_value.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Revenue by Month */}
                    {revenueData.revenue_by_month.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={revenueData.revenue_by_month}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="revenue" stroke="#0891B2" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Top Customers */}
                    {revenueData.revenue_by_customer.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Top Customers</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={revenueData.revenue_by_customer.slice(0, 5)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="customer" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="revenue" fill="#0891B2" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* Data Table */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="font-semibold">Revenue by Customer</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Customer
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Revenue
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {revenueData.revenue_by_customer.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-sm text-gray-900">{item.customer}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                ${item.revenue.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Production Tab */}
              {activeTab === 'production' && productionData && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                      <p className="text-sm text-gray-600 mb-1">Total Batches</p>
                      <p className="text-2xl font-bold text-gray-900">{productionData.total_batches}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <p className="text-sm text-gray-600 mb-1">Total Volume</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {productionData.total_volume.toFixed(1)} BBL
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Avg Batch Size</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {productionData.average_batch_size.toFixed(1)} BBL
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <p className="text-sm text-gray-600 mb-1">Brewing</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {productionData.batches_by_status['Brewing'] || 0}
                      </p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Batches by Style */}
                    {productionData.batches_by_style.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Batches by Style</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie 
                              data={productionData.batches_by_style} 
                              cx="50%"
                              cy="50%"
                              dataKey="count" 
                              nameKey="name" 
                              innerRadius={60} 
                              outerRadius={100}
                            >
                              {productionData.batches_by_style.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={(entry as any).fill || COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Volume by Style */}
                    {productionData.batches_by_style.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Volume by Style</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={productionData.batches_by_style}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="volume" fill="#F59E0B" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Inventory Tab */}
              {activeTab === 'inventory' && inventoryData && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <p className="text-sm text-gray-600 mb-1">Total Items</p>
                      <p className="text-2xl font-bold text-gray-900">{inventoryData.total_items}</p>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4 border border-cyan-200">
                      <p className="text-sm text-gray-600 mb-1">Total Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${inventoryData.total_value.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-gray-600 mb-1">Low Stock Items</p>
                      <p className="text-2xl font-bold text-gray-900">{inventoryData.low_stock_count}</p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Items by Category */}
                    {inventoryData.items_by_category.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Items by Category</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie 
                              data={inventoryData.items_by_category} 
                              cx="50%"
                              cy="50%"
                              dataKey="count" 
                              nameKey="category" 
                              innerRadius={60} 
                              outerRadius={100}
                            >
                              {inventoryData.items_by_category.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={(entry as any).fill || COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Top Items by Value */}
                    {inventoryData.top_items_by_value.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Top Items by Value</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={inventoryData.top_items_by_value.slice(0, 8)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} height={100} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10B981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sales Tab */}
              {activeTab === 'sales' && salesData && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                      <p className="text-2xl font-bold text-gray-900">{salesData.total_orders}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${salesData.total_revenue.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <p className="text-sm text-gray-600 mb-1">Avg Order Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${salesData.average_order_value.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Customers */}
                    {salesData.top_customers.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Top Customers</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={salesData.top_customers.slice(0, 5)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="customer" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="revenue" fill="#0891B2" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Top Products */}
                    {salesData.top_products.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Top Products</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={salesData.top_products.slice(0, 5)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="product" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="quantity" fill="#10B981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* Data Table */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="font-semibold">Top Customers by Revenue</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Customer
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Orders
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Revenue
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {salesData.top_customers.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-sm text-gray-900">{item.customer}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                {item.orders}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                ${item.revenue.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reporting;
